import { UserSession } from "@/types/session";
import { AgentRouting, ChatResponse, StreamEvent } from "@/types/chat";
import type { WorkspacePersona } from "@/config/workspacePersona";
import { supabase } from "@/lib/supabase";

class ChatService {
  private currentWebhookUrl: string;
  private webhookUrl: string;
  private webhookStubUrl: string;

  constructor() {
    this.webhookUrl = import.meta.env.VITE_WEBHOOK_URL;
    this.webhookStubUrl = import.meta.env.VITE_WEBHOOK_TEST_URL;
    this.currentWebhookUrl = this.webhookUrl;
  }

  toggleWebhook = () => {
    this.currentWebhookUrl = this.currentWebhookUrl === this.webhookUrl ? this.webhookStubUrl : this.webhookUrl;
    console.log("Chat service current webhook URL:", this.currentWebhookUrl);
  };

  sendMessage = async ({
    message,
    context,
    session,
    agent_routing,
    workspace_persona,
  }: {
    message: string;
    context?: string;
    session: UserSession;
    agent_routing?: AgentRouting;
    workspace_persona?: WorkspacePersona;
  }): Promise<ChatResponse> => {
    if (!this.currentWebhookUrl?.trim()) {
      throw new Error(
        "VITE_WEBHOOK_URL is not set. Set it to the educator-wellbeing edge function URL in .env and restart the dev server. See .env.example and docs/LOCAL_DEV.md.",
      );
    }

    const payload = {
      message,
      context,
      sessionId: session.id,
      userId: session.user_id,
      timestamp: new Date().toISOString(),
      ...(agent_routing && { agent_routing }),
      ...(workspace_persona && { workspace_persona }),
    };

    if (import.meta.env.DEV) {
      console.info("[HeyBodhi chat] POST educator-wellbeing", {
        context: payload.context,
        workspace_persona: payload.workspace_persona ?? "(not sent)",
        agent_routing: payload.agent_routing ?? "(default)",
        userId: payload.userId ? `${payload.userId.slice(0, 8)}…` : "(missing)",
      });
    }

    const { data: { session: authSession } } = await supabase.auth.getSession();
    const response = await fetch(this.currentWebhookUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authSession?.access_token ? { Authorization: `Bearer ${authSession.access_token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data: unknown = await response.json();
    return data as ChatResponse;
  };

  async *sendMessageStream({
    message,
    context,
    session,
    agent_routing,
    workspace_persona,
  }: {
    message: string;
    context?: string;
    session: UserSession;
    agent_routing?: AgentRouting;
    workspace_persona?: WorkspacePersona;
  }): AsyncGenerator<StreamEvent> {
    if (!this.currentWebhookUrl?.trim()) {
      throw new Error(
        "VITE_WEBHOOK_URL is not set. Set it to the educator-wellbeing edge function URL in .env and restart the dev server. See .env.example and docs/LOCAL_DEV.md.",
      );
    }

    const payload = {
      message,
      context,
      sessionId: session.id,
      userId: session.user_id,
      timestamp: new Date().toISOString(),
      ...(agent_routing && { agent_routing }),
      ...(workspace_persona && { workspace_persona }),
    };

    const { data: { session: authSession } } = await supabase.auth.getSession();
    const response = await fetch(this.currentWebhookUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(authSession?.access_token ? { Authorization: `Bearer ${authSession.access_token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok || !response.body) throw new Error(`HTTP error! status: ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.trim()) continue;
        yield JSON.parse(line) as StreamEvent;
      }
    }

    if (buffer.trim()) {
      yield JSON.parse(buffer) as StreamEvent;
    }
  }
}

export const chatService = new ChatService();
